import {Flag} from 'platform/components';
import {Box, Heading, HStack, Icon, Show, Text, ThemeColorPath, VStack} from 'platform/foundation';
import {match} from 'ts-pattern';

import i18n from '@dms/i18n';

import {Nullish, suffixTestId, TestIdProps} from 'shared/web';

import {useAuctionCountdown} from '../../../hooks/useAuctionCountdown';
import {formatAuctionEndDate} from '../utils/formatAuctionEndDate';

type CountdownState = 'running' | 'endingSoon' | 'extended' | 'ended';

const PAD_LENGTH = 2;

interface AuctionCountdownCardProps extends TestIdProps {
  carEndTime: string | Nullish;
  isEnded: boolean;
}

/**
 * How long the lot still runs. A lot extended by late bids keeps its stale end time, which is
 * then shown as the original end next to the "Extended" badge instead of a countdown at zero.
 */
export function AuctionCountdownCard(props: AuctionCountdownCardProps) {
  const countdown = useAuctionCountdown(props.carEndTime, props.isEnded);

  if (!countdown.hasEndTime && !countdown.isEnded) {
    return null;
  }

  const state = match(countdown)
    .returnType<CountdownState>()
    .with({isEnded: true}, () => 'ended')
    .with({hasElapsed: true}, () => 'extended')
    // The shared countdown stops ticking at 0s, which can happen a fraction of a second before
    // the end time passes - an active lot at zero is past its listed end, i.e. extended.
    .with({hasEndTime: true, days: 0, hours: 0, minutes: 0, seconds: 0}, () => 'extended')
    .with({isEndingSoon: true}, () => 'endingSoon')
    .otherwise(() => 'running');

  const backgroundColor = match(state)
    .returnType<ThemeColorPath>()
    .with('ended', () => 'palettes.neutral.20.100')
    .with('extended', 'endingSoon', () => 'palettes.orange.10.100')
    .otherwise(() => 'palettes.green.10.100');

  const title = match(state)
    .with('ended', () => i18n.t('entity.auction.status.ended'))
    .with('extended', () => i18n.t('entity.auction.status.extended'))
    .otherwise(() => i18n.t('entity.auction.labels.endsIn'));

  const endDate = countdown.hasEndTime ? formatAuctionEndDate(props.carEndTime ?? '') : null;
  const isTicking = state === 'running' || state === 'endingSoon';

  const segments = [
    {key: 'days', value: countdown.days, unit: i18n.t('general.metric.d')},
    {key: 'hours', value: countdown.hours, unit: i18n.t('general.metric.h')},
    {key: 'minutes', value: countdown.minutes, unit: i18n.t('general.metric.m')},
    {key: 'seconds', value: countdown.seconds, unit: i18n.t('general.metric.s')},
  ];

  return (
    <Box
      backgroundColor={backgroundColor}
      borderRadius="medium"
      padding={3}
      data-testid={props['data-testid']}
    >
      <HStack justify="space-between" align="center" spacing={3} wrap>
        <VStack spacing={1}>
          <HStack spacing={2} align="center" wrap>
            <Icon value="action/schedule" size={4} color="text.secondary" />
            <Text size="small" alternative data-testid={suffixTestId('title', props)}>
              {title}
            </Text>
            <Show when={state === 'endingSoon'}>
              <Flag
                label={i18n.t('entity.auction.status.endingSoon')}
                colorScheme="orange"
                size="small"
                data-testid={suffixTestId('endingSoon', props)}
              />
            </Show>
          </HStack>
          <Show when={endDate}>
            {(date) => (
              <Text size="xSmall" color="secondary" data-testid={suffixTestId('endDate', props)}>
                {isTicking
                  ? i18n.t('entity.auction.labels.auctionEndsAt', {date})
                  : `${i18n.t('entity.auction.labels.auctionEnd')}: ${date}`}
              </Text>
            )}
          </Show>
        </VStack>
        <Show when={isTicking}>
          <HStack spacing={3} align="center" wrap>
            {segments.map((segment) => (
              <HStack key={segment.key} spacing={1} align="center">
                <Heading
                  size={2}
                  isSingleLine
                  data-testid={suffixTestId(`countdownValue-${segment.key}`, props)}
                >
                  {segment.value.toString().padStart(PAD_LENGTH, '0')}
                </Heading>
                <Text size="small" color="secondary">
                  {segment.unit}
                </Text>
              </HStack>
            ))}
          </HStack>
        </Show>
      </HStack>
    </Box>
  );
}
